import React, { useEffect, useMemo, useState } from "react";
import SuperbillService from "../../superbill/SuperbillService";
import AppointmentsService from "../../appointments/appointmentService";
import DiagnosisCombinerService from "../../Authorization/diagnosisCombinerService";
import DoctorInfoService from "../../doctorInfo/doctorInfoService";
import OfficeService from "../../office/officeService";

const PatientSuperbills = ({ patientId, authorizations = [] }) => {
  const [superbills, setSuperbills] = useState([]);
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [viewMode, setViewMode] = useState("card");
  const [currentPage, setCurrentPage] = useState(0);

  const superbillService = new SuperbillService();
  const appointmentService = new AppointmentsService();
  const diagService = new DiagnosisCombinerService();
  const doctorService = new DoctorInfoService();
  const officeService = new OfficeService();

  useEffect(() => {
    const loadSuperbills = async () => {
      if (!patientId) return;

      try {
        setLoading(true);
        setError("");
        setSuperbills([]);
        setDetails({});
        setCurrentPage(0);

        const allBills = [];
        const detailMap = {};

        for (const auth of authorizations || []) {
          const authId = auth.authId || auth.authorizationId || auth.id;
          if (!authId) continue;

          let diagCombId = auth.diagCombId;

          if (!diagCombId) {
            try {
              const diagResult = await diagService.getByAuthId(authId);

              const diagRecord = Array.isArray(diagResult)
                ? diagResult[0]
                : diagResult;

              diagCombId =
                diagRecord?.diagCombId ||
                diagRecord?.diagComb_id ||
                null;
            } catch {
              diagCombId = null;
            }
          }

          if (!diagCombId) continue;

          const oldAppointments =
            await appointmentService.getAppointmentsByDiagComb(diagCombId);

          for (const appt of oldAppointments || []) {
            const appointmentId = appt.appoinmentId || appt.appointmentId;

            if (!appointmentId) continue;

            const bills = await superbillService.getByAppointment(appointmentId);

            let doctorName = "Unknown doctor";
            let officeName = "Unknown office";

            try {
              const doctor = await doctorService.getDoctorById(appt.doctorId);

              doctorName =
                `${doctor.firstname || doctor.firstName || ""} ${
                  doctor.lastname || doctor.lastName || ""
                }`.trim() || `Doctor #${appt.doctorId}`;
            } catch {
              doctorName = appt.doctorId ? `Doctor #${appt.doctorId}` : "N/A";
            }

            try {
              const office = await officeService.getOfficeById(appt.officeId);
              officeName = office?.officeName || `Office #${appt.officeId}`;
            } catch {
              officeName = appt.officeId ? `Office #${appt.officeId}` : "N/A";
            }

            for (const bill of bills || []) {
              allBills.push(bill);

              detailMap[bill.superbillId] = {
                authorization: auth,
                appointment: appt,
                doctorName,
                officeName,
              };
            }
          }
        }

        const sortedBills = allBills.sort((a, b) => {
          const dateA = a.superbillDate || "";
          const dateB = b.superbillDate || "";

          if (dateA === dateB) {
            return (a.superbillId || 0) - (b.superbillId || 0);
          }

          return dateB.localeCompare(dateA);
        });

        setSuperbills(sortedBills);
        setDetails(detailMap);
      } catch (err) {
        console.error("Failed to load patient superbills:", err);
        setError("Could not load superbills.");
      } finally {
        setLoading(false);
      }
    };

    loadSuperbills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, authorizations]);

  useEffect(() => {
    if (currentPage >= superbills.length) {
      setCurrentPage(0);
    }
  }, [superbills, currentPage]);

  const formatDate = (value) => {
    if (!value) return "N/A";

    const parts = String(value).split("-");

    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${month}-${day}-${year}`;
  };

  const formatTime12Hour = (value) => {
    if (!value) return "N/A";

    const parts = String(value).split(":");
    const hour = parseInt(parts[0], 10);
    const minute = parts[1] || "00";

    if (Number.isNaN(hour)) return value;

    const suffix = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minute} ${suffix}`;
  };

  const formatTypeVisit = (typeVisit) => {
    if (!typeVisit) return "N/A";

    const normalized = String(typeVisit).trim().toLowerCase();

    if (normalized === "progress note") return "PN";
    if (normalized === "discharge") return "DIS";

    return typeVisit;
  };

  const isSimpleNoAuthorizationCode = (authCode) => {
    if (!authCode) return false;

    const cleaned = String(authCode).trim();

    if (!/^\d+$/.test(cleaned)) {
      return false;
    }

    const number = parseInt(cleaned, 10);

    return number >= 1 && number <= 99;
  };

  const currentBill = superbills[currentPage] || null;
  const currentDetail = currentBill ? details[currentBill.superbillId] : null;
  const currentAppointment = currentDetail?.appointment;
  const currentAuth = currentDetail?.authorization;

  const hideAuthInfo = isSimpleNoAuthorizationCode(currentAuth?.authCode);

  const dateGrid = useMemo(() => {
    return superbills.map((bill, index) => {
      const detail = details[bill.superbillId];
      const appt = detail?.appointment;

      return {
        superbillId: bill.superbillId,
        date: bill.superbillDate,
        billed: bill.billed,
        notesCompleted: bill.notesCompleted,
        typeVisit: appt?.typeVisit,
        index,
      };
    });
  }, [superbills, details]);

  const goPrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const goNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, superbills.length - 1));
  };

  const openDateFromGrid = (index) => {
    setCurrentPage(index);
    setViewMode("card");
  };

  return (
    <div className="info-box">
      <h3>Superbills</h3>

      {loading && <p>Loading superbills...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && superbills.length === 0 && <p>No superbills found.</p>}

      {superbills.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode("card")}
              style={{
                padding: "6px 12px",
                borderRadius: "999px",
                border: "1px solid #d1d5db",
                background: viewMode === "card" ? "#2563eb" : "#ffffff",
                color: viewMode === "card" ? "#ffffff" : "#111827",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              One Per Page
            </button>

            <button
              type="button"
              onClick={() => setViewMode("grid")}
              style={{
                padding: "6px 12px",
                borderRadius: "999px",
                border: "1px solid #d1d5db",
                background: viewMode === "grid" ? "#2563eb" : "#ffffff",
                color: viewMode === "grid" ? "#ffffff" : "#111827",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Date Grid
            </button>

            <span style={{ fontSize: "0.9rem", color: "#4b5563" }}>
              Total Superbills: <strong>{superbills.length}</strong>
            </span>
          </div>

          {viewMode === "grid" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                gap: "8px",
              }}
            >
              {dateGrid.map((item) => {
                const complete = item.billed && item.notesCompleted;
                const needsWork = !item.billed || !item.notesCompleted;

                return (
                  <button
                    key={item.superbillId}
                    type="button"
                    onClick={() => openDateFromGrid(item.index)}
                    style={{
                      border: needsWork
                        ? "1px solid #f59e0b"
                        : "1px solid #d1d5db",
                      borderRadius: "10px",
                      padding: "10px",
                      background: complete ? "#dcfce7" : "#fff7ed",
                      color: "#111827",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <strong>{formatDate(item.date)}</strong>

                    <div style={{ fontSize: "0.8rem", marginTop: "4px" }}>
                      Type: {formatTypeVisit(item.typeVisit)}
                    </div>

                    <div style={{ fontSize: "0.8rem" }}>
                      Billed: {item.billed ? "Yes" : "No"}
                    </div>

                    <div style={{ fontSize: "0.8rem" }}>
                      Notes: {item.notesCompleted ? "Yes" : "No"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {viewMode === "card" && currentBill && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={goPrevious}
                  disabled={currentPage === 0}
                >
                  Previous
                </button>

                <span>
                  Superbill <strong>{currentPage + 1}</strong> of{" "}
                  <strong>{superbills.length}</strong>
                </span>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={currentPage + 1 >= superbills.length}
                >
                  Next
                </button>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "12px",
                  background: "#ffffff",
                }}
              >
                <p>
                  <strong>Superbill Date:</strong>{" "}
                  {formatDate(currentBill.superbillDate)}
                </p>

                <p>
                  <strong>Superbill ID:</strong>{" "}
                  {currentBill.superbillId || "N/A"}
                </p>

                <p>
                  <strong>Old Appointment ID:</strong>{" "}
                  {currentBill.appointmentId || "N/A"}
                </p>

                <p>
                  <strong>Office:</strong>{" "}
                  {currentDetail?.officeName || "Loading..."}
                </p>

                <p>
                  <strong>Doctor:</strong>{" "}
                  {currentDetail?.doctorName || "Loading..."}
                </p>

                <p>
                  <strong>Appointment Date:</strong>{" "}
                  {formatDate(currentAppointment?.appointmentDate)}
                </p>

                <p>
                  <strong>Appointment Time:</strong>{" "}
                  {formatTime12Hour(currentAppointment?.appointmentTime)}
                </p>

                <p>
                  <strong>Type Visit:</strong>{" "}
                  {formatTypeVisit(currentAppointment?.typeVisit)}
                </p>

                {hideAuthInfo ? (
                  <p>
                    <strong>Authorization:</strong> No formal authorization
                    required
                  </p>
                ) : (
                  <>
                    <p>
                      <strong>Authorization Code:</strong>{" "}
                      {currentAuth?.authCode || "N/A"}
                    </p>

                    <p>
                      <strong>Auth Start:</strong>{" "}
                      {formatDate(currentAuth?.dayStart)}
                    </p>

                    <p>
                      <strong>Auth End:</strong>{" "}
                      {formatDate(currentAuth?.dayEnd)}
                    </p>

                    <p>
                      <strong>Total Visits:</strong>{" "}
                      {currentAuth?.totalVisits ?? "N/A"}
                    </p>

                    <p>
                      <strong>Remaining Visits:</strong>{" "}
                      {currentAuth?.visits ?? "N/A"}
                    </p>
                  </>
                )}

                <p>
                  <strong>Billed:</strong>{" "}
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      background: currentBill.billed ? "#dcfce7" : "#fee2e2",
                      color: currentBill.billed ? "#166534" : "#991b1b",
                      fontWeight: "bold",
                    }}
                  >
                    {currentBill.billed ? "Yes" : "No"}
                  </span>
                </p>

                <p>
                  <strong>Notes Completed:</strong>{" "}
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      background: currentBill.notesCompleted
                        ? "#dcfce7"
                        : "#fee2e2",
                      color: currentBill.notesCompleted ? "#166534" : "#991b1b",
                      fontWeight: "bold",
                    }}
                  >
                    {currentBill.notesCompleted ? "Yes" : "No"}
                  </span>
                </p>

                <p>
                  <strong>Appointment Notes:</strong>{" "}
                  {currentAppointment?.notes || "N/A"}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PatientSuperbills;